import { Box, Button, DialogContent, Divider, Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react'
import WorkflowGroupClient, { WorkflowGroupContext, WorkflowGroupDetail, WorkflowGroupErrors } from '../apiClients/WorkflowGroupClient';
import { NameIdEmailSlim, NameIdSlim } from '../models/custom-models';
import IconButtonEx from '../components/IconButtonEx';
import { useErrorDisplay } from '../components/ErrorDisplay';
import DeleteIcon from '@mui/icons-material/Delete';
import SelectItemPopup from '../components/SelectItemPopup'
import BusySpinner from '../components/BusySpinner';
import TableHeaderCell from '../components/TableHeaderCell'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { IfTrue, IsEmpty, IsNullOrUndefined } from '../Helpers';
import ButtonEx from '../components/ButtonEx';
import SaveIcon from '@mui/icons-material/Save';
import { useSnackBarDisplay } from '../components/SnackBarDisplay'
import OnTrue from '../components/OnTrue';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { Securables } from '../Enums';
import TZGravatar from '../components/TZGravatar';
import { useAccessInfo } from '../contexts/AccessTokenProvider';
import PanelSegment from '../components/PanelSegment';
import { useMainDisplayHeight } from '../components/WindowDimensions';
import { I18N } from '../I18N';
import PageTitle from '../components/PageTitle';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import useViewportMode from '../hooks/useViewportMode';
import { MobileEntityCard, ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from '../components/responsive';

const getNewContext = () => {
    return {
        Name: '',
        Description: ''
    } as WorkflowGroupContext;
}

export interface WorkflowGroupManagementProps {
    embedded?: boolean;
}

const WorkflowGroupManagement = ({ embedded = false }: WorkflowGroupManagementProps) => {
    const theme = useTheme();
    const initialized = useRef<boolean>();
    const [groupContext, setGroupContext] = useState<WorkflowGroupContext>(getNewContext());
    const [groupList, setGroupList] = useState<NameIdSlim[]>([]);
    const [groupDetail, setGroupDetail] = useState<WorkflowGroupDetail>();
    const [selectedGroupId, setSelectedGroupId] = useState<string>();
    const [showAddPopup, setShowAddPopup] = useState<boolean>(false);
    const [busy, setBusy] = useState<boolean>(false);
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [SnackBarDisplay, setMessage] = useSnackBarDisplay();
    const { hasRole } = useAccessInfo();
    const { isPhone, isTablet, isDesktop, isPortrait } = useViewportMode();
    const compactDrillIn = isPhone || (isTablet && isPortrait);
    const mainDisplayHeight = useMainDisplayHeight();
    const showGroupList = !compactDrillIn || IsNullOrUndefined(groupDetail);
    const showGroupDetail = !compactDrillIn || !IsNullOrUndefined(groupDetail);

    const loadData = async () => {
        try {
            setBusy(true);
            setGroupList(await new WorkflowGroupClient().GetAll());
        } catch (error) {
            setError(error, WorkflowGroupErrors.GetAll);
        } finally {
            setBusy(false);
        }
    }

    useEffect(() => {
        if (initialized.current === true)
            return;
        initialized.current = true;
        loadData();
    });

    const handleDeleteGroup = async (groupId: string) => {
        try {
            setBusy(true);
            await new WorkflowGroupClient().Delete(groupId);
            if (selectedGroupId === groupId) {
                console.log('deleting current group');
                setSelectedGroupId(undefined);
                setGroupDetail(undefined);
            }

            await loadData();
        } catch (error) {
            setError(error, WorkflowGroupErrors.Delete);
        } finally {
            setBusy(false);
        }
    }

    const handleSelectedGroup = async (groupId: string) => {
        try {
            setBusy(true);
            setSelectedGroupId(groupId);
            let gd = await new WorkflowGroupClient().GetSingle(groupId);

            setGroupDetail({
                Id: '',
                Name: '',
                Description: '',
                Members: [],
                //addon properties
                modified: false
            });

            setGroupDetail(gd);
        } catch (error) {
            setError(error, WorkflowGroupErrors.GetSingle);
        } finally {
            setBusy(false);
        }
    }

    const handleShowAdd = async () => {
        setGroupContext(getNewContext());
        setShowAddPopup(true);
    }

    const handleAdd = async () => {
        if (IsEmpty(groupContext.Name))
            return;
        try {
            setBusy(true);
            var groupId = await new WorkflowGroupClient().Create(groupContext);
            await loadData();
            handleSelectedGroup(groupId);
            setShowAddPopup(false);
            setMessage('Group was created sucessfully', false);
        } catch (error) {
            setError(error, WorkflowGroupErrors.Create);
        } finally {
            setBusy(false);
        }
    }

    const handleAddUsersToGroup = async (groupId: string, items: NameIdEmailSlim[]) => {
        try {
            setBusy(true);
            let client = new WorkflowGroupClient();
            for (let i = 0; i < items.length; i++) {
                client.AddTalentToGroup(groupId, items[i].Id);
            }
            groupDetail.Members = [...groupDetail.Members, ...items];
            setGroupDetail({ ...groupDetail });
            setMessage('User was added', false);
        } catch (error) {
            setError(error, "Failed to add users to group")
        } finally {
            setBusy(false);
        }
    }

    const handleRemoveUserFromGroup = async (groupId: string, userId: string) => {
        try {
            setBusy(true);
            await new WorkflowGroupClient().RemoveTalentFromGroup(groupId, userId);
            groupDetail.Members.splice(groupDetail.Members.findIndex(x => x.Id === userId), 1);
            setGroupDetail({ ...groupDetail });
            setMessage('User was removed', false);
        } catch (error) {
            setError(error, "Failed to add users to group")
        } finally {
            setBusy(false);
        }
    }

    const handleSaveModifications = async () => {
        try {
            setBusy(true);
            await new WorkflowGroupClient().Update(groupDetail.Id, { Name: groupDetail.Name, Description: groupDetail.Description });
            setGroupDetail({ ...groupDetail, modified: false });
            setMessage('Group was updated', false);
        } catch (error) {
            setError(error, "Failed to save modifications");
        } finally {
            setBusy(false);
        }
    }

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <ErrorDisplay />
            <BusySpinner busy={busy}>
            <PanelSegment titleContent={embedded ? undefined : <PageTitle title={I18N.Translate('Workflow Group Management')} />}
                    toolbar={<OnTrue test={hasRole(Securables.organization_group_create)}><IconButtonEx hoverContent={I18N.Translate('Add Group')} visibleLabel={I18N.Translate('Add Group')} aria-label={I18N.Translate('Add Group')} size='small' onClick={handleShowAdd} /></OnTrue>
                        
                    }
                    fillHeight>
                    <Box sx={{ height: embedded ? '100%' : isDesktop ? mainDisplayHeight : 'auto', minHeight: embedded || isDesktop ? 0 : 'calc(100dvh - 128px)', width: '100%' }}>
                {/* <Paper sx={{
                    width: '100%',
                    minHeight: '300px',
                    borderRadius: '5px',
                }}
                    elevation={5}
                    style={{ position: 'absolute', height: '100%' }}> */}
                    
                    {/* <Divider /> */}
                    <Grid container sx={{ height: embedded ? '100%' : isDesktop ? mainDisplayHeight : 'auto', minHeight: 0 }}>
                        <Grid
                            item
                            sx={{
                                display: showGroupList ? 'block' : 'none',
                                width: compactDrillIn ? '100%' : isTablet ? '32%' : '240px',
                                minWidth: 0,
                            }}
                        >
                            <Box border={1} borderColor={theme.palette.divider} height={isDesktop ? mainDisplayHeight : '100%'}>
                                {!isDesktop ? (
                                    <Stack component="nav" aria-label="Workflow groups" spacing={1} sx={{ p: 1.5 }}>
                                        {groupList.map((group) => (
                                            <Button
                                                key={group.Id}
                                                variant={group.Id === selectedGroupId ? 'contained' : 'outlined'}
                                                onClick={() => handleSelectedGroup(group.Id)}
                                                sx={{ minHeight: 48, justifyContent: 'flex-start', textAlign: 'left' }}
                                            >
                                                {group.Name}
                                            </Button>
                                        ))}
                                        {groupList.length === 0 && <Typography color="text.secondary">No groups yet.</Typography>}
                                    </Stack>
                                ) : <TableContainer>
                                    <Table stickyHeader sx={{ minWidth: 100, border: 0, borderCOlor: 'red' }} size="small" aria-label="a dense table">
                                        <TableBody>
                                            {groupList.map((group) => (
                                                <TableRow key={group.Id} selected={group.Id === selectedGroupId}>
                                                    <TableCell onClick={() => handleSelectedGroup(group.Id)}>{group.Name}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>}
                            </Box>
                        </Grid>
                        <Grid item xs sx={{ display: showGroupDetail ? 'block' : 'none', minWidth: 0 }}>
                            {IfTrue(!IsNullOrUndefined(groupDetail), () =>
                                <Grid container height='100%' sx={{ minHeight: 0 }}>
                                    <Grid item xs={true} sm={true}>
                                        <Box border={1} borderRadius='3px' borderColor={theme.palette.divider} padding={theme.spacing(isDesktop ? 1 : 2)} height="100%">
                                            {compactDrillIn && (
                                                <Button
                                                    startIcon={<ArrowBackRoundedIcon />}
                                                    onClick={() => {
                                                        setSelectedGroupId(undefined);
                                                        setGroupDetail(undefined);
                                                    }}
                                                    sx={{ minHeight: 44, mb: 1 }}
                                                >
                                                    All groups
                                                </Button>
                                            )}
                                            <div style={{ height: '15px' }} />
                                            <TextField
                                                required
                                                fullWidth
                                                label="Group Name"
                                                placeholder="Name of your Group..."
                                                variant="outlined"
                                                value={groupDetail.Name ?? ''}
                                                disabled={!hasRole(Securables.organization_group_edit)}
                                                onChange={(event) => {
                                                    if (!hasRole(Securables.organization_group_edit))
                                                        return;
                                                    setGroupDetail({ ...groupDetail, Name: event.target.value, modified: true });
                                                }} />
                                            <div style={{ height: '15px' }} />
                                            <TextField
                                                required
                                                fullWidth
                                                label="Description"
                                                placeholder="group description..."
                                                variant="outlined"
                                                value={groupDetail.Description ?? ''}
                                                disabled={!hasRole(Securables.organization_group_edit)}
                                                onChange={(event) => {
                                                    if (!hasRole(Securables.organization_group_edit))
                                                        return;
                                                    setGroupDetail({ ...groupDetail, Description: event.target.value, modified: true });
                                                }} />
                                            <div style={{ height: '15px' }} />
                                            {IfTrue(groupDetail.modified, () =>
                                                <div style={{ textAlign: 'right', marginBottom: '15px' }}>
                                                    <ButtonEx disabled={busy} startIcon={<SaveIcon htmlColor='blue' fontSize='small' />} variant='contained' hoverContent='Save Changes' size='small' onClick={handleSaveModifications}>Save Changes</ButtonEx>
                                                </div>
                                            )}
                                            <Box border={1} borderRadius='3px' borderColor={theme.palette.divider} padding={0}>
                                                {!isDesktop ? (
                                                    <Stack spacing={1.25} sx={{ p: 1.5 }}>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>Group Members</Typography>
                                                            <OnTrue test={hasRole(Securables.organization_group_delete)}>
                                                                <SelectItemPopup title="Select Users" icon={<GroupAddIcon htmlColor='lightblue' />} hoverContent={I18N.Translate('Add Talent to Group')} visibleLabel={I18N.Translate('Add')} aria-label={I18N.Translate('Add Talent to Group')} itemLoader={() => new WorkflowGroupClient().GetAvailableTalent(groupDetail.Id)} onSelectItems={items => handleAddUsersToGroup(groupDetail.Id, items as NameIdEmailSlim[])} />
                                                            </OnTrue>
                                                        </Stack>
                                                        {groupDetail.Members.map((member) => (
                                                            <MobileEntityCard
                                                                key={member.Id}
                                                                title={member.Name}
                                                                subtitle={member.Email}
                                                                status={<TZGravatar email={member.Email} />}
                                                                ariaLabel={`Group member: ${member.Name}`}
                                                                actions={hasRole(Securables.organization_group_edit) ? (
                                                                    <IconButtonEx hoverContent={`Remove ${member.Name}`} size='small' message='Are you sure you want to remove this Talent' onClick={() => handleRemoveUserFromGroup(groupDetail.Id, member.Id)}><RemoveCircleOutlineIcon htmlColor='red' fontSize='small' /></IconButtonEx>
                                                                ) : undefined}
                                                            />
                                                        ))}
                                                        {groupDetail.Members.length === 0 && <Typography color="text.secondary">No members in this group.</Typography>}
                                                    </Stack>
                                                ) : <TableContainer>
                                                    <Table stickyHeader sx={{ minWidth: 100, borderCOlor: 'red' }} size="small" aria-label="a dense table">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableHeaderCell colspan={3}>
                                                                    Group Members
                                                                </TableHeaderCell>
                                                                <TableHeaderCell style={{ width: '96px' }}>
                                                                    <OnTrue test={hasRole(Securables.organization_group_delete)}>
                                                                        <SelectItemPopup title="Select Users" icon={<GroupAddIcon htmlColor='lightblue' />} hoverContent={I18N.Translate('Add Talent to Group')} visibleLabel={I18N.Translate('Add')} aria-label={I18N.Translate('Add Talent to Group')} itemLoader={() => new WorkflowGroupClient().GetAvailableTalent(groupDetail.Id)} onSelectItems={items => handleAddUsersToGroup(groupDetail.Id, items as NameIdEmailSlim[])} />
                                                                    </OnTrue>
                                                                </TableHeaderCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {groupDetail.Members.map((m) => (
                                                                <TableRow key={m.Id}>
                                                                    <TableCell width='15px'><TZGravatar email={m.Email} /></TableCell>
                                                                    <TableCell>{m.Name}</TableCell>
                                                                    <TableCell style={{ width: '25px' }}>
                                                                        <OnTrue test={hasRole(Securables.organization_group_edit)}><IconButtonEx hoverContent='Remove' size='small' message='Are you sure you want to remove this Talent' onClick={() => handleRemoveUserFromGroup(groupDetail.Id, m.Id)}><RemoveCircleOutlineIcon htmlColor='red' fontSize='small' /></IconButtonEx></OnTrue>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>}
                                            </Box>
                                            {!isDesktop && hasRole(Securables.organization_group_delete) && (
                                                <ButtonEx
                                                    message='Are you sure you want to delete this group?'
                                                    startIcon={<DeleteIcon htmlColor='red' fontSize='small' />}
                                                    onClick={(event) => { event.preventDefault(); handleDeleteGroup(groupDetail.Id); }}
                                                    sx={{ minHeight: 44, mt: 2 }}
                                                >
                                                    Delete Group
                                                </ButtonEx>
                                            )}
                                        </Box>
                                    </Grid>
                                    <Grid item sx={{ width: '200px', display: isDesktop ? 'block' : 'none' }}>
                                        <Box border={1} borderRadius='3px' borderColor={theme.palette.divider} padding="0" height='100%'>
                                            <Stack spacing={1}>
                                                <Typography sx={{ padding: '5px' }}>Actions:</Typography>
                                                <Divider />
                                                <OnTrue test={hasRole(Securables.organization_group_delete)}><ButtonEx message='Are you sure you want to delete this group?' startIcon={<DeleteIcon htmlColor='red' fontSize='small' />} onClick={(e) => { e.preventDefault(); handleDeleteGroup(groupDetail.Id); }}>Delete Group</ButtonEx></OnTrue>

                                            </Stack>
                                        </Box>
                                    </Grid>
                                </Grid>

                            )}
                        </Grid>
                    </Grid>
                    </Box>
                </PanelSegment>
            </BusySpinner>
            <ResponsiveDialog aria-labelledby="customized-dialog-title" open={showAddPopup} fullWidth onClose={() => setShowAddPopup(false)}>
                <BusySpinner busy={busy}>
                    <ResponsiveDialogHeader id="customized-dialog-title" title="Create Group" onClose={() => setShowAddPopup(false)} />
                    <DialogContent dividers sx={{ padding: theme.spacing(2) }}>
                        <div>
                            <TextField
                                required
                                fullWidth
                                label="Name"
                                placeholder="Name of your Group..."
                                variant="outlined"
                                value={groupContext.Name ?? ''}
                                onChange={(event) => setGroupContext({ ...groupContext, Name: event.target.value })} />
                            <div style={{ height: '15px' }} />
                            <TextField
                                required
                                fullWidth
                                label="Description"
                                placeholder="group description..."
                                variant="outlined"
                                value={groupContext.Description ?? ''}
                                onChange={(event) => setGroupContext({ ...groupContext, Description: event.target.value })} />
                        </div>
                    </DialogContent>
                    <StickyDialogActions viewportOverride={isPhone ? 'phone' : isTablet ? 'tablet' : 'desktop'}>
                        <Button disabled={busy} onClick={() => setShowAddPopup(false)} variant='text' color='inherit'>Cancel</Button>
                        <Button disabled={busy || IsEmpty(groupContext.Name)} onClick={handleAdd} variant='outlined' color='primary'>Create</Button>
                    </StickyDialogActions>
                </BusySpinner>
            </ResponsiveDialog>
            <SnackBarDisplay />
        </div>
    );
}

export default WorkflowGroupManagement;
